import { PrivateRoute } from 'components/PrivateRoute';
import { RegionRedirect } from 'components/RegionRedirect';
import { Routes } from 'constants/pages';
import { useFlags } from 'launchdarkly-react-client-sdk';
import AppPageTemplate from 'layouts/AppPageTemplate';
import * as Pages from 'pages';
import settingsRoutes from 'pages/Settings/routes';
import { shipmentsRoutes } from 'pages/Shipments/routes';
import React from 'react';
import { Navigate, RouteObject, useRoutes } from 'react-router-dom';

const SHORTCUT_REDIRECTS = {
  '/c': Routes.ContainerTracking,
  '/cp': Routes.ComponentPreview,
  '/ea': Routes.EmptyAppointments,
  '/ed': Routes.EmptyAppointments,
  '/pda': Routes.PerDiemFighter,
  '/ers': Routes.EmptyReturnLocations,
  '/t': Routes.EmptyReturnLocations,
  '/vs': Routes.VesselSchedule,
  '/gs': Routes.GateSchedule,
  '/s': Routes.Settings,
  '/credentials': Routes.Credentials,
  '/notification-settings': Routes.NotificationSettings,
};

const SHORTCUT_ROUTE_OBJECTS = Object.entries(SHORTCUT_REDIRECTS).map(
  ([shortcutUrl, redirectTo]: [string, string]) => ({
    path: shortcutUrl,
    elements: <Navigate replace to={redirectTo} />,
  })
);

const AppRoutes = () => {
  const flags = useFlags();
  const routes: RouteObject[] = React.useMemo(() => {
    if (flags.maintenance?.enabled) {
      return [{ path: '*', element: <Pages.Maintenance /> }];
    }

    // The default list of routes to use with React Router
    const routeObjects: RouteObject[] = [
      {
        path: Routes.Home,
        element: <Pages.SignIn />,
      },
      {
        path: Routes.TermsOfUse,
        element: <Pages.TermsOfUse />,
      },
      {
        path: Routes.PrivacyPolicy,
        element: <Pages.PrivacyPolicy />,
      },
      {
        path: Routes.ContactUs,
        element: <Pages.ContactUs />,
      },
      {
        path: Routes.SignUp,
        element: <Pages.SignUp />,
      },
      {
        path: Routes.SignIn,
        element: <Pages.SignIn />,
      },
      {
        path: Routes.Waitlist,
        element: (
          <PrivateRoute grouping="signup">
            <Pages.Waitlist />
          </PrivateRoute>
        ),
      },
      {
        path: Routes.ResetPassword,
        element: <Pages.ResetPassword />,
      },
      {
        path: Routes.ChangePassword,
        element: <Pages.ChangePassword />,
      },
      {
        path: Routes.FreeTable,
        element: <Pages.FreeTable />,
      },
      {
        path: Routes.EmailConfirmation,
        element: <Pages.EmailConfirmation />,
      },
      {
        path: Routes.Welcome,
        element: <Pages.Welcome />,
      },
      {
        path: Routes.ForceLogin,
        element: <Pages.ForceLogin />,
      },
      {
        path: Routes.JoinCompany,
        element: (
          <PrivateRoute grouping="signup">
            <Pages.JoinCompany />
          </PrivateRoute>
        ),
      },
      {
        path: Routes.AwaitingAdmin,
        element: (
          <PrivateRoute grouping="signup">
            <Pages.AwaitingAdmin />
          </PrivateRoute>
        ),
      },
      {
        path: Routes.CreateCompany,
        element: (
          <PrivateRoute grouping="signup">
            <Pages.CreateCompany />
          </PrivateRoute>
        ),
      },
      {
        path: '*',
        element: <Pages.NotFound404 />,
      },
      {
        path: '/',
        element: (
          <PrivateRoute>
            <AppPageTemplate />
          </PrivateRoute>
        ),
        children: [
          {
            path: Routes.Landing,
            element: <Pages.Landing />,
          },
          {
            path: Routes.EmptyReturnLocations,
            element: <RegionRedirect redirectTo={Routes.RegionalEmptyReturnLocations} />,
          },
          {
            path: Routes.VesselSchedule,
            element: <Pages.VesselSchedule />,
          },
          {
            path: Routes.GateSchedule,
            element: <RegionRedirect redirectTo={Routes.RegionalGateSchedule} />,
          },
          {
            path: Routes.EmptyAppointments,
            element: <RegionRedirect redirectTo={Routes.RegionalEmptyAppointments} />,
          },
          {
            path: Routes.TerminalScreenshots,
            element: <RegionRedirect redirectTo={Routes.RegionalTerminalScreenshots} />,
          },
          {
            path: Routes.PerDiemFighter,
            element: <RegionRedirect redirectTo={Routes.RegionalPerDiemArchive} />,
          },
          {
            path: Routes.PerDiemFighterCalculator,
            element: <Pages.PerDiemFighterCalculator />,
          },
          {
            path: Routes.Settings,
            element: <Pages.Settings />,
            children: settingsRoutes(),
          },
          {
            path: Routes.JoinRequestValidation,
            element: <Pages.JoinRequestValidation />,
          },
          {
            path: Routes.ComponentPreview,
            element: <Pages.ComponentPreview />,
          },
          {
            path: Routes.ContainerTracking,
            element: <Pages.TrackAndTrace />,
            children: [
              {
                path: ':containerNumber',
                element: <Pages.ContainerDetailsPage />,
              },
            ],
          },
          {
            path: Routes.Shipments,
            element: <Pages.Shipments />,
            children: shipmentsRoutes(),
          },
          // Hopefully one day these components can move to Retool or an internal dashboard
          {
            path: Routes.AdminGateSchedule,
            element: <Pages.GateSchedulesContainer />,
          },
          {
            path: Routes.AdminPtscSchedule,
            element: <Pages.PtscScheduleContainer />,
          },
          ...SHORTCUT_ROUTE_OBJECTS,
          {
            path: '/:region',
            children: [
              {
                path: Routes.RegionalEmptyAppointments,
                element: <Pages.Appointments />,
              },
              {
                path: Routes.RegionalPerDiemArchive,
                element: <Pages.PerDiemArchive />,
              },
              {
                path: Routes.RegionalTerminalScreenshots,
                element: <Pages.TerminalScreenshots />,
              },
              {
                path: Routes.RegionalGateSchedule,
                element: <Pages.GateSchedule />,
              },
              {
                path: Routes.RegionalEmptyReturnLocations,
                element: <Pages.Terminals />,
              },
              {
                path: '',
                element: <Pages.NotFound404 />,
              },
            ],
          },
        ],
      },
    ];

    return routeObjects;
  }, [flags]);

  return useRoutes(routes);
};

export default AppRoutes;
